// ============================================================================
// KLINISOL - Clinical Research Resources Page
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// Pure Tailwind CSS - Accordion-style sections
// ============================================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronDown, ChevronUp, FileText, Shield, Clipboard, AlertCircle, Database, Activity } from 'lucide-react';

const Resources = () => {
    const [openSection, setOpenSection] = useState(null);

    const toggleSection = (section) => {
        setOpenSection(openSection === section ? null : section);
    };

    const sections = [
        {
            id: 'iit',
            icon: FileText,
            title: 'What is an Investigator-Initiated Trial (IIT)?',
            content: (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        An Investigator-Initiated Trial (IIT) is a clinical study conceived, designed, and conducted by independent researchers or clinicians, rather than being sponsored by pharmaceutical or device companies. These trials are driven by scientific curiosity and clinical need, often exploring innovative therapeutic approaches, off-label uses of approved treatments, or addressing unmet medical needs in specific patient populations.
                    </p>
                    
                    <h4 className="font-bold text-[#475569] mt-4">Key Characteristics of IITs:</h4>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4">
                        <li><strong>Investigator Control:</strong> The principal investigator maintains scientific and operational control over the study design, conduct, and publication</li>
                        <li><strong>Academic Focus:</strong> Often conducted at academic medical centers with emphasis on scientific discovery rather than commercial objectives</li>
                        <li><strong>Regulatory Responsibility:</strong> The investigator assumes sponsor responsibilities under FDA regulations (21 CFR 312.3)</li>
                        <li><strong>Funding Sources:</strong> May be supported by government grants (NIH, DoD), foundations, institutional funds, or industry partnerships</li>
                    </ul>

                    <h4 className="font-bold text-[#475569] mt-4">Differences from Sponsor-Initiated Trials:</h4>
                    <div className="bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/5 rounded-lg p-4 border border-gray-200">
                        <p className="text-sm text-gray-700 mb-2"><strong>Sponsor-Initiated Trials:</strong> Industry sponsors maintain control, provide resources, and bear regulatory responsibility. Focus is often on regulatory approval and commercialization.</p>
                        <p className="text-sm text-gray-700"><strong>Investigator-Initiated Trials:</strong> Investigators drive the science, assume sponsor responsibilities, and often focus on questions beyond commercial interest—such as comparative effectiveness, special populations, or mechanistic studies.</p>
                    </div>

                    <h4 className="font-bold text-[#475569] mt-4">Benefits of IITs for Clinical Innovation:</h4>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4">
                        <li>Address research questions that may not align with commercial priorities</li>
                        <li>Explore novel therapeutic approaches in specific patient populations</li>
                        <li>Generate pilot data to support larger confirmatory studies</li>
                        <li>Advance academic careers through independent research leadership</li>
                        <li>Contribute to medical knowledge and improve patient care</li>
                    </ul>

                    <h4 className="font-bold text-[#475569] mt-4">Common Therapeutic Areas for IITs:</h4>
                    <p className="text-gray-700">
                        KLIN<span className="text-[#E91E63]">i</span>SOL specializes in supporting IITs across <strong>dermatology</strong> (psoriasis, eczema, wound healing), <strong>cardiovascular</strong> (heart failure, arrhythmias), and <strong>gastroenterology</strong> (IBD, NASH, celiac disease), where investigator-driven research plays a critical role in advancing treatment paradigms.
                    </p>
                </div>
            )
        },
        {
            id: 'fda',
            icon: Shield,
            title: 'FDA Regulatory Pathway Overview',
            content: (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        Understanding the FDA regulatory pathway is essential for conducting compliant investigator-initiated trials. The regulatory requirements depend on whether your study involves drugs, biologics, or devices.
                    </p>

                    <h4 className="font-bold text-[#475569] mt-4">IND (Investigational New Drug) Application Process:</h4>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4">
                        <li><strong>Pre-IND Meeting:</strong> Optional but recommended consultation with FDA to discuss study design and regulatory strategy (21 CFR 312.82)</li>
                        <li><strong>IND Submission:</strong> Submit IND application 30 days before first patient enrollment, including protocol, investigator brochure, chemistry/manufacturing data, and animal toxicology</li>
                        <li><strong>FDA Review:</strong> FDA has 30 days to review and may place study on clinical hold if safety concerns arise</li>
                        <li><strong>Annual Reports:</strong> Submit annual IND safety reports and study progress updates</li>
                        <li><strong>Safety Reporting:</strong> Report serious adverse events (SAEs) within required timelines (7-15 days depending on severity)</li>
                    </ul>

                    <h4 className="font-bold text-[#475569] mt-4">IDE (Investigational Device Exemption) for Device Studies:</h4>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4">
                        <li>Required for significant risk devices under 21 CFR Part 812</li>
                        <li>Non-significant risk devices may proceed with IRB approval only</li>
                        <li>IDE application includes device description, manufacturing information, and proposed clinical protocol</li>
                    </ul>

                    <h4 className="font-bold text-[#475569] mt-4">Key Regulations:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-4 border-2 border-[#E91E63]">
                            <p className="font-bold text-[#E91E63] mb-2">21 CFR Part 312</p>
                            <p className="text-sm text-gray-700">Investigational New Drug (IND) applications for drugs and biologics</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border-2 border-[#E91E63]">
                            <p className="font-bold text-[#E91E63] mb-2">21 CFR Part 812</p>
                            <p className="text-sm text-gray-700">Investigational Device Exemption (IDE) for medical devices</p>
                        </div>
                    </div>

                    <h4 className="font-bold text-[#475569] mt-4">Timeline and Milestones:</h4>
                    <div className="bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/5 rounded-lg p-4 border border-gray-200">
                        <ul className="space-y-2 text-sm text-gray-700">
                            <li><strong>Months 0-3:</strong> Protocol development, pre-IND meeting (optional)</li>
                            <li><strong>Month 4:</strong> IND/IDE submission</li>
                            <li><strong>Month 5:</strong> FDA review period (30 days); IRB submission</li>
                            <li><strong>Month 6:</strong> First patient enrollment (if no clinical hold)</li>
                            <li><strong>Ongoing:</strong> Safety reporting, protocol amendments, annual reports</li>
                        </ul>
                    </div>

                    <h4 className="font-bold text-[#475569] mt-4">Common Regulatory Challenges:</h4>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4">
                        <li>Insufficient non-clinical safety data to support first-in-human studies</li>
                        <li>Inadequate manufacturing documentation (CMC) for investigational products</li>
                        <li>Protocol design issues (endpoints, safety monitoring, statistical power)</li>
                        <li>Incomplete informed consent forms that don't meet regulatory requirements</li>
                    </ul>

                    <p className="text-gray-700 mt-4">
                        <strong>KLIN<span className="text-[#E91E63]">i</span>SOL Support:</strong> Our regulatory affairs team has extensive experience navigating IND/IDE applications, FDA communications, and maintaining compliance throughout the trial lifecycle.
                    </p>
                </div>
            )
        },
        {
            id: 'protocol',
            icon: Clipboard,
            title: 'Protocol Development Best Practices',
            content: (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        A well-designed clinical protocol is the foundation of a successful investigator-initiated trial. It serves as the blueprint for study conduct and the primary document reviewed by IRBs, regulatory agencies, and funding organizations.
                    </p>

                    <h4 className="font-bold text-[#475569] mt-4">Essential Protocol Components:</h4>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4">
                        <li><strong>Background and Rationale:</strong> Scientific justification and clinical need</li>
                        <li><strong>Objectives and Endpoints:</strong> Primary and secondary outcomes with clear definitions</li>
                        <li><strong>Study Design:</strong> Phase, randomization, blinding, control group</li>
                        <li><strong>Study Population:</strong> Inclusion/exclusion criteria with clear rationale</li>
                        <li><strong>Intervention:</strong> Detailed description of investigational product, dosing, administration</li>
                        <li><strong>Assessments and Procedures:</strong> Visit schedule, measurements, data collection</li>
                        <li><strong>Safety Monitoring:</strong> AE/SAE definitions, reporting procedures, stopping rules</li>
                        <li><strong>Statistical Analysis Plan:</strong> Sample size justification, analytical methods</li>
                        <li><strong>Ethical Considerations:</strong> Risk/benefit assessment, informed consent process</li>
                    </ul>

                    <h4 className="font-bold text-[#475569] mt-4">Statistical Considerations:</h4>
                    <div className="bg-white rounded-lg p-4 border-2 border-[#E91E63]">
                        <p className="text-gray-700 mb-3"><strong>Key Statistical Elements:</strong></p>
                        <ul className="space-y-2 text-sm text-gray-700">
                            <li>✓ Clearly defined primary endpoint with measurable outcomes</li>
                            <li>✓ Sample size calculation based on expected effect size, power (typically 80-90%), and significance level (α = 0.05)</li>
                            <li>✓ Planned interim analyses (if applicable) with adjustment for multiple testing</li>
                            <li>✓ Handling of missing data and sensitivity analyses</li>
                            <li>✓ Subgroup analyses pre-specified to avoid data dredging</li>
                        </ul>
                    </div>

                    <h4 className="font-bold text-[#475569] mt-4">Endpoint Selection:</h4>
                    <p className="text-gray-700">
                        Choose endpoints that are clinically meaningful, measurable, and feasible. Consider:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4">
                        <li><strong>Clinical vs. Surrogate:</strong> Clinical endpoints (e.g., mortality, quality of life) are preferred but may require larger samples</li>
                        <li><strong>Objective vs. Subjective:</strong> Validated assessment tools reduce bias and improve reproducibility</li>
                        <li><strong>Feasibility:</strong> Ensure endpoints can be reliably measured within study constraints</li>
                    </ul>

                    <h4 className="font-bold text-[#475569] mt-4">Risk Mitigation Strategies:</h4>
                    <div className="bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/5 rounded-lg p-4 border border-gray-200">
                        <ul className="space-y-2 text-sm text-gray-700">
                            <li><strong>Data Safety Monitoring Board (DSMB):</strong> Independent oversight for high-risk or long-duration studies</li>
                            <li><strong>Stopping Rules:</strong> Pre-defined criteria for early termination due to safety or futility</li>
                            <li><strong>Run-In Period:</strong> Assess compliance and tolerability before randomization</li>
                            <li><strong>Adaptive Designs:</strong> Allow protocol modifications based on interim data (requires careful statistical planning)</li>
                        </ul>
                    </div>

                    <h4 className="font-bold text-[#475569] mt-4">Common Protocol Pitfalls to Avoid:</h4>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4">
                        <li>Overly restrictive inclusion/exclusion criteria that impede enrollment</li>
                        <li>Inadequate safety monitoring plans for high-risk interventions</li>
                        <li>Unrealistic timelines and visit schedules that burden participants</li>
                        <li>Poorly defined endpoints that are difficult to measure consistently</li>
                        <li>Insufficient detail in procedures, leading to protocol deviations</li>
                    </ul>

                    <p className="text-gray-700 mt-4">
                        <strong>KLIN<span className="text-[#E91E63]">i</span>SOL Support:</strong> Our protocol development team works collaboratively with investigators to design rigorous, feasible protocols that meet regulatory standards and scientific objectives.
                    </p>
                </div>
            )
        },
        {
            id: 'gcp',
            icon: AlertCircle,
            title: 'ICH-GCP E6(R3) Guidelines Summary',
            content: (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        The International Council for Harmonisation Good Clinical Practice (ICH-GCP) E6(R3) guidelines represent the international ethical and scientific quality standard for designing, conducting, recording, and reporting clinical trials. Compliance with ICH-GCP is required for trials that support regulatory submissions.
                    </p>

                    <h4 className="font-bold text-[#475569] mt-4">Core ICH-GCP Principles:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-4 border-2 border-[#E91E63]">
                            <p className="font-bold text-[#E91E63] mb-2">Principle 1</p>
                            <p className="text-sm text-gray-700">Trials must be conducted in accordance with ethical principles that have their origin in the Declaration of Helsinki</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border-2 border-[#E91E63]">
                            <p className="font-bold text-[#E91E63] mb-2">Principle 2</p>
                            <p className="text-sm text-gray-700">Risks to participants must be minimized and justified by anticipated benefits</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border-2 border-[#E91E63]">
                            <p className="font-bold text-[#E91E63] mb-2">Principle 3</p>
                            <p className="text-sm text-gray-700">Participant rights, safety, and well-being are the most important considerations</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border-2 border-[#E91E63]">
                            <p className="font-bold text-[#E91E63] mb-2">Principle 4</p>
                            <p className="text-sm text-gray-700">Trial must be scientifically sound and described in a clear, detailed protocol</p>
                        </div>
                    </div>

                    <h4 className="font-bold text-[#475569] mt-4">Key Updates in E6(R3) (2023):</h4>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4">
                        <li><strong>Risk-Based Approach:</strong> Greater emphasis on risk-proportionate monitoring and quality management</li>
                        <li><strong>Technology Integration:</strong> Recognition of electronic systems, remote monitoring, and digital health technologies</li>
                        <li><strong>Quality Management:</strong> Shift from document verification to critical process and data integrity focus</li>
                        <li><strong>Diversity and Inclusion:</strong> Enhanced guidance on ensuring representative participant populations</li>
                        <li><strong>Data Integrity:</strong> Strengthened requirements for data reliability and traceability</li>
                    </ul>

                    <h4 className="font-bold text-[#475569] mt-4">Investigator Responsibilities Under ICH-GCP:</h4>
                    <div className="bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/5 rounded-lg p-4 border border-gray-200">
                        <ul className="space-y-2 text-sm text-gray-700">
                            <li>✓ Ensure trial is conducted according to approved protocol</li>
                            <li>✓ Obtain and document freely given informed consent</li>
                            <li>✓ Report adverse events per protocol and regulations</li>
                            <li>✓ Maintain adequate and accurate source documents</li>
                            <li>✓ Ensure investigational product accountability</li>
                            <li>✓ Communicate deviations and safety information to IRB</li>
                            <li>✓ Supervise delegated trial tasks appropriately</li>
                        </ul>
                    </div>

                    <h4 className="font-bold text-[#475569] mt-4">Documentation Requirements:</h4>
                    <p className="text-gray-700">Essential documents required for regulatory inspection:</p>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4">
                        <li>IRB/IEC approval letters and protocol amendments</li>
                        <li>Informed consent forms (signed and dated)</li>
                        <li>Investigator brochure and product labeling</li>
                        <li>Source documents supporting CRF entries</li>
                        <li>Delegation of authority log</li>
                        <li>Monitoring visit logs and correspondence</li>
                        <li>SAE reports and safety communications</li>
                    </ul>

                    <h4 className="font-bold text-[#475569] mt-4">Quality Management System:</h4>
                    <p className="text-gray-700">
                        E6(R3) emphasizes a systematic approach to quality management focusing on critical-to-quality factors rather than exhaustive documentation. This includes:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4">
                        <li>Risk identification and mitigation strategies</li>
                        <li>Proportionate monitoring based on risk assessment</li>
                        <li>Key risk indicators (KRIs) and quality tolerance limits</li>
                        <li>Root cause analysis and corrective/preventive actions (CAPA)</li>
                    </ul>

                    <p className="text-gray-700 mt-4">
                        <strong>KLIN<span className="text-[#E91E63]">i</span>SOL Compliance:</strong> All KLIN<span className="text-[#E91E63]">i</span>SOL operations are conducted in full compliance with ICH-GCP E6(R3) principles, with comprehensive training programs for investigators and site personnel.
                    </p>
                </div>
            )
        },
        {
            id: 'data',
            icon: Database,
            title: 'Data Management & 21 CFR Part 11 Compliance',
            content: (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        Electronic data capture and management systems used in clinical trials must comply with FDA 21 CFR Part 11 regulations, which establish requirements for electronic records and electronic signatures to be considered trustworthy, reliable, and equivalent to paper records.
                    </p>

                    <h4 className="font-bold text-[#475569] mt-4">21 CFR Part 11 Electronic Records Requirements:</h4>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4">
                        <li><strong>Validation:</strong> Systems must be validated to ensure accuracy, reliability, and consistent intended performance</li>
                        <li><strong>Audit Trails:</strong> All data changes must be logged with user ID, timestamp, and reason for change</li>
                        <li><strong>Access Control:</strong> Unique user authentication and role-based permissions</li>
                        <li><strong>Data Integrity:</strong> Electronic records must be protected from alteration or deletion</li>
                        <li><strong>Electronic Signatures:</strong> Must be unique, non-reusable, and linked to specific records</li>
                    </ul>

                    <h4 className="font-bold text-[#475569] mt-4">Audit Trail Essentials:</h4>
                    <div className="bg-white rounded-lg p-4 border-2 border-[#E91E63]">
                        <p className="text-gray-700 mb-3"><strong>Every audit trail entry must include:</strong></p>
                        <ul className="space-y-2 text-sm text-gray-700">
                            <li>• User identification (who performed the action)</li>
                            <li>• Date and time stamp (when the action occurred)</li>
                            <li>• Action performed (what was done)</li>
                            <li>• Original value and new value (what changed)</li>
                            <li>• Reason for change (why the modification was made)</li>
                        </ul>
                    </div>

                    <h4 className="font-bold text-[#475569] mt-4">Data Integrity Principles (ALCOA++):</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-gradient-to-br from-[#475569]/10 to-[#E91E63]/10 rounded-lg p-3 text-center border border-[#E91E63]/30 hover:border-[#E91E63] transition-all">
                            <p className="font-bold text-[#E91E63]">Attributable</p>
                            <p className="text-xs text-gray-700 mt-1">Who created it</p>
                        </div>
                        <div className="bg-gradient-to-br from-[#475569]/10 to-[#E91E63]/10 rounded-lg p-3 text-center border border-[#E91E63]/30 hover:border-[#E91E63] transition-all">
                            <p className="font-bold text-[#E91E63]">Legible</p>
                            <p className="text-xs text-gray-700 mt-1">Readable forever</p>
                        </div>
                        <div className="bg-gradient-to-br from-[#475569]/10 to-[#E91E63]/10 rounded-lg p-3 text-center border border-[#E91E63]/30 hover:border-[#E91E63] transition-all">
                            <p className="font-bold text-[#E91E63]">Contemporaneous</p>
                            <p className="text-xs text-gray-700 mt-1">Created in real-time</p>
                        </div>
                        <div className="bg-gradient-to-br from-[#475569]/10 to-[#E91E63]/10 rounded-lg p-3 text-center border border-[#E91E63]/30 hover:border-[#E91E63] transition-all">
                            <p className="font-bold text-[#E91E63]">Original</p>
                            <p className="text-xs text-gray-700 mt-1">First recording</p>
                        </div>
                        <div className="bg-gradient-to-br from-[#475569]/10 to-[#E91E63]/10 rounded-lg p-3 text-center border border-[#E91E63]/30 hover:border-[#E91E63] transition-all">
                            <p className="font-bold text-[#E91E63]">Accurate</p>
                            <p className="text-xs text-gray-700 mt-1">Free from errors</p>
                        </div>
                        <div className="bg-gradient-to-br from-[#475569]/10 to-[#E91E63]/10 rounded-lg p-3 text-center border border-[#E91E63]/30 hover:border-[#E91E63] transition-all">
                            <p className="font-bold text-[#E91E63]">Complete</p>
                            <p className="text-xs text-gray-700 mt-1">All data present</p>
                        </div>
                        <div className="bg-gradient-to-br from-[#475569]/10 to-[#E91E63]/10 rounded-lg p-3 text-center border border-[#E91E63]/30 hover:border-[#E91E63] transition-all">
                            <p className="font-bold text-[#E91E63]">Consistent</p>
                            <p className="text-xs text-gray-700 mt-1">Logical sequence</p>
                        </div>
                        <div className="bg-gradient-to-br from-[#475569]/10 to-[#E91E63]/10 rounded-lg p-3 text-center border border-[#E91E63]/30 hover:border-[#E91E63] transition-all">
                            <p className="font-bold text-[#E91E63]">Enduring</p>
                            <p className="text-xs text-gray-700 mt-1">Preserved long-term</p>
                        </div>
                    </div>

                    <h4 className="font-bold text-[#475569] mt-4">Data Management Best Practices:</h4>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4">
                        <li><strong>Data Validation:</strong> Implement edit checks and range validations at point of entry</li>
                        <li><strong>Query Management:</strong> Systematic review and resolution of data discrepancies</li>
                        <li><strong>Database Lock:</strong> Formal process to freeze database before analysis</li>
                        <li><strong>Data Transfer:</strong> Use secure, validated methods for data exports and transfers</li>
                        <li><strong>Archival:</strong> Maintain electronic records for minimum 7 years post-study completion</li>
                    </ul>

                    <h4 className="font-bold text-[#475569] mt-4">Common Data Integrity Violations:</h4>
                    <div className="bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/5 rounded-lg p-4 border border-gray-200">
                        <ul className="space-y-2 text-sm text-gray-700">
                            <li>✗ Backdating data entries</li>
                            <li>✗ Sharing login credentials</li>
                            <li>✗ Modifying data without audit trail</li>
                            <li>✗ Inadequate training on system use</li>
                            <li>✗ Failure to document reason for changes</li>
                        </ul>
                    </div>

                    <p className="text-gray-700 mt-4">
                        <strong>KLIN<span className="text-[#E91E63]">i</span>SOL EDC Platform:</strong> Our Electronic Data Capture system is fully validated and 21 CFR Part 11 compliant, featuring comprehensive audit trails, role-based access control, electronic signatures, and secure data archival. Learn more about the <Link to="/ilo-platform" className="text-[#E91E63] underline hover:text-[#475569]">ILO Platform</Link>.
                    </p>
                </div>
            )
        },
        {
            id: 'safety',
            icon: Activity,
            title: 'Safety Reporting & Pharmacovigilance',
            content: (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        Participant safety is the highest priority in clinical research. Investigators must understand adverse event definitions, reporting timelines, and pharmacovigilance responsibilities to ensure compliance with FDA regulations and ethical standards.
                    </p>

                    <h4 className="font-bold text-[#475569] mt-4">Adverse Event (AE) vs. Serious Adverse Event (SAE) Definitions:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-4 border-2 border-[#475569]">
                            <p className="font-bold text-[#475569] mb-2">Adverse Event (AE)</p>
                            <p className="text-sm text-gray-700">Any untoward medical occurrence in a clinical trial participant, whether or not considered related to the investigational product. This includes:</p>
                            <ul className="text-xs text-gray-600 mt-2 space-y-1">
                                <li>• New illness or injury</li>
                                <li>• Worsening of pre-existing condition</li>
                                <li>• Laboratory abnormality</li>
                                <li>• Symptom or sign</li>
                            </ul>
                        </div>
                        <div className="bg-white rounded-lg p-4 border-2 border-[#E91E63]">
                            <p className="font-bold text-[#E91E63] mb-2">Serious Adverse Event (SAE)</p>
                            <p className="text-sm text-gray-700">An AE that meets one or more of the following criteria:</p>
                            <ul className="text-xs text-gray-600 mt-2 space-y-1">
                                <li>• Results in death</li>
                                <li>• Life-threatening</li>
                                <li>• Requires hospitalization</li>
                                <li>• Persistent/significant disability</li>
                                <li>• Congenital anomaly</li>
                                <li>• Important medical event</li>
                            </ul>
                        </div>
                    </div>

                    <h4 className="font-bold text-[#475569] mt-4">FDA Reporting Timelines (21 CFR 312.32):</h4>
                    <div className="bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/5 rounded-lg p-4 border border-gray-200">
                        <ul className="space-y-3 text-sm text-gray-700">
                            <li><strong className="text-[#E91E63]">7 Calendar Days:</strong> Fatal or life-threatening suspected unexpected serious adverse reactions (SUSARs)</li>
                            <li><strong className="text-[#475569]">15 Calendar Days:</strong> All other SUSARs (serious, unexpected, and possibly related to investigational product)</li>
                            <li><strong>Annual Report:</strong> Summary of all adverse events, regardless of seriousness or causality</li>
                            <li><strong>Immediate:</strong> Safety issues requiring protocol modification or study suspension</li>
                        </ul>
                    </div>

                    <h4 className="font-bold text-[#475569] mt-4">Causality Assessment:</h4>
                    <p className="text-gray-700">Investigators must assess the relationship between adverse events and investigational product:</p>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4">
                        <li><strong>Unrelated:</strong> No temporal relationship or alternative etiology clearly explains event</li>
                        <li><strong>Unlikely:</strong> Temporal relationship exists but other factors more likely responsible</li>
                        <li><strong>Possible:</strong> Temporal relationship exists, causality uncertain</li>
                        <li><strong>Probable:</strong> Temporal relationship with reasonable likelihood of causality</li>
                        <li><strong>Definite:</strong> Clear causal relationship with positive dechallenge/rechallenge</li>
                    </ul>

                    <h4 className="font-bold text-[#475569] mt-4">Safety Monitoring Plans:</h4>
                    <p className="text-gray-700">Comprehensive safety monitoring should include:</p>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4">
                        <li><strong>Data Safety Monitoring Board (DSMB):</strong> Independent committee for high-risk or long-duration studies</li>
                        <li><strong>Safety Run-In Period:</strong> Initial cohort to assess tolerability before full enrollment</li>
                        <li><strong>Stopping Rules:</strong> Pre-defined criteria for early termination (safety or futility)</li>
                        <li><strong>Aggregate Safety Reviews:</strong> Periodic review of all safety data by medical monitor</li>
                    </ul>

                    <h4 className="font-bold text-[#475569] mt-4">IRB Safety Reporting:</h4>
                    <div className="bg-white rounded-lg p-4 border-2 border-[#E91E63]">
                        <p className="text-gray-700 mb-2">Report to IRB within required timelines:</p>
                        <ul className="space-y-2 text-sm text-gray-700">
                            <li>• Unanticipated problems involving risks to participants or others</li>
                            <li>• Serious or continuing noncompliance</li>
                            <li>• Suspension or termination of IRB approval</li>
                            <li>• Protocol deviations affecting safety or scientific integrity</li>
                        </ul>
                    </div>

                    <h4 className="font-bold text-[#475569] mt-4">Documentation Requirements:</h4>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 pl-4">
                        <li>Source documentation of AE onset, resolution, and clinical course</li>
                        <li>SAE report forms completed within 24 hours of awareness</li>
                        <li>Follow-up reports until resolution or stabilization</li>
                        <li>Causality assessment documented with clinical rationale</li>
                    </ul>

                    <p className="text-gray-700 mt-4">
                        <strong>KLIN<span className="text-[#E91E63]">i</span>SOL Support:</strong> Our pharmacovigilance team provides 24/7 safety monitoring, expedited reporting, medical review, and regulatory communication to ensure participant safety and compliance.
                    </p>
                </div>
            )
        }
    ];

    return (
        <div className="min-h-screen bg-[#f8fafd]">
            {/* Hero Section */}
            <section className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-16 px-6">
                <div className="max-w-5xl mx-auto text-center">
                    <BookOpen className="w-16 h-16 mx-auto mb-4" />
                    <h1 className="text-4xl md:text-5xl font-bold mb-3">
                        Research Resources
                    </h1>
                    <p className="text-lg opacity-90">
                        Expert insights and regulatory guidance for investigator-initiated trials
                    </p>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-12 px-6">
                <div className="max-w-5xl mx-auto">
                    {/* Introduction */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 mb-8">
                        <p className="text-gray-700 leading-relaxed">
                            KLIN<span className="text-[#E91E63]">i</span>SOL provides comprehensive educational resources to support investigators conducting clinical research. Our 30 years of experience in FDA-regulated trials across dermatology, cardiovascular, and gastroenterology therapeutic areas informs these practical guidelines and best practices.
                        </p>
                    </div>

                    {/* Accordion Sections */}
                    <div className="space-y-4">
                        {sections.map((section) => {
                            const IconComponent = section.icon;
                            const isOpen = openSection === section.id;
                            
                            return (
                                <div 
                                    key={section.id}
                                    className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg hover:border-[#E91E63] transition-all"
                                >
                                    <button
                                        onClick={() => toggleSection(section.id)}
                                        className="w-full p-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                                    >
                                        <div className="flex items-center gap-4">
                                            <IconComponent className="w-8 h-8 text-[#E91E63] flex-shrink-0" />
                                            <h2 className="text-xl font-bold text-gray-900">
                                                {section.title}
                                            </h2>
                                        </div>
                                        {isOpen ? (
                                            <ChevronUp className="w-6 h-6 text-[#E91E63] flex-shrink-0" />
                                        ) : (
                                            <ChevronDown className="w-6 h-6 text-[#E91E63] flex-shrink-0" />
                                        )}
                                    </button>
                                    
                                    {isOpen && (
                                        <div className="px-6 pb-6 border-t border-gray-200">
                                            <div className="pt-6">
                                                {section.content}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* CTA Section */}
                    <div className="mt-12 bg-gradient-to-br from-[#475569]/10 to-[#E91E63]/10 rounded-xl p-8 text-center border border-gray-200">
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">
                            Need Support for Your Investigator-Initiated Trial?
                        </h3>
                        <p className="text-gray-700 mb-6">
                            Our team is ready to help you navigate the complexities of clinical research
                        </p>
                        <Link 
                            to="/for-investigators" 
                            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#475569] to-[#E91E63] text-white font-bold rounded-lg hover:from-[#334155] hover:to-[#C2185B] transition-all shadow-lg"
                        >
                            <FileText className="w-6 h-6" />
                            Learn About Our Services
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-6 px-6 mt-12">
                <div className="max-w-7xl mx-auto text-center">
                    <h3 className="text-xl font-bold mb-2">KLINiSOL</h3>
                    <p className="text-sm opacity-90 mb-2">
                        &copy; 2025 KLINISOL. All rights reserved.
                    </p>
                    <Link 
                        to="/terms-and-conditions" 
                        className="text-xs opacity-75 hover:opacity-100 hover:underline transition-opacity mb-4 inline-block"
                    >
                        Terms & Conditions
                    </Link>
                    
                    {/* Social Media Links */}
                    <div className="flex items-center justify-center gap-4 mt-4">
                        <a 
                            href="https://www.linkedin.com/company/klinisol/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110"
                            aria-label="LinkedIn"
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                            </svg>
                        </a>
                        
                        <a 
                            href="https://youtube.com/@klinisol721" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110"
                            aria-label="YouTube"
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                            </svg>
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Resources;